// src/verticals/general/widgets/GeneralCertificatesWidget.tsx
//
// "Certifications" — the credentials the signed-in learner has earned across the
// general (Rehearse) catalog. Reads the real `certificates` table directly from
// Supabase (RLS scopes it to the current user), joins the course title, and
// renders each as an editorial credential row. "View" opens the shared
// Certificate modal (print / share / verify) — no new certificate plumbing.
//
// Empty state is continuity-framed (design philosophy): it points at the next
// action — finish a program — rather than showing a blank card.
import { useEffect, useState } from "react";
import { Award, ExternalLink } from "lucide-react";
import { supabase } from "@/shared/lib/supabase";
import { useLanguage } from "@/shared/i18n/LanguageContext";
import { useT } from "@/shared/i18n/useT";
import { Certificate } from "@/shared/components/dashboard/Certificate";
import type { Certificate as CertificateType } from "@/shared/hooks/useCourseProgress";
import { registerWidget } from "@/shared/dashboard/widgets/registry";
import type { WidgetProps } from "@/shared/dashboard/widgets/types";

interface CertRow extends CertificateType {
  courses?: { title?: string; title_uk?: string } | null;
}

const CERT_COLUMNS =
  "id, user_id, course_id, user_full_name, user_email, certificate_number, issued_at, total_score, total_time_spent_minutes, completion_date, is_public, share_token, courses(title, title_uk)";

function courseTitle(row: CertRow, locale: string): string {
  const c = row.courses;
  if (!c) return "Program";
  return (
    (locale === "uk" ? c.title_uk : c.title) ??
    c.title ??
    c.title_uk ??
    "Program"
  );
}

function formatDate(value: string | null, locale: string): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(locale === "uk" ? "uk-UA" : locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function GeneralCertificatesWidgetImpl(_: WidgetProps) {
  const t = useT();
  const { locale } = useLanguage();
  const [rows, setRows] = useState<CertRow[] | null>(null);
  const [viewing, setViewing] = useState<CertRow | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        if (!cancelled) setRows([]);
        return;
      }
      const { data, error } = await supabase
        .from("certificates")
        .select(CERT_COLUMNS)
        .order("issued_at", { ascending: false });
      if (cancelled) return;
      if (error) {
        console.error("GeneralCertificatesWidget fetch failed:", error);
        setRows([]);
      } else {
        setRows((data as unknown as CertRow[]) ?? []);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loading = rows === null;

  return (
    <section className="rounded-md border border-[var(--hairline)] bg-[var(--paper)] p-5">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h3 className="text-[15px] font-semibold text-[var(--ink)]">
          {t("dash.certs.heading")}
        </h3>
        {!loading && rows.length > 0 && (
          <span className="ed-mono text-[10px] text-[var(--muted)]">
            {rows.length}
          </span>
        )}
      </div>

      {loading ? (
        <div className="space-y-2.5">
          {[0, 1].map((i) => (
            <div
              key={i}
              className="h-14 animate-pulse rounded-[6px] bg-[rgba(12,26,51,0.05)]"
            />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-[6px] border border-dashed border-[var(--hairline)] px-4 py-6 text-center">
          <Award
            size={20}
            className="mx-auto text-[var(--muted)]"
            aria-hidden="true"
          />
          <p className="mt-2 text-sm text-[var(--muted)]">
            {t("dash.certs.empty")}
          </p>
        </div>
      ) : (
        <ul className="space-y-2.5">
          {rows.map((row) => {
            const title = courseTitle(row, locale);
            const date = formatDate(
              row.completion_date ?? row.issued_at,
              locale,
            );
            return (
              <li key={row.id}>
                <button
                  type="button"
                  onClick={() => setViewing(row)}
                  className="group flex w-full items-center gap-3 rounded-[6px] border border-[var(--hairline)] bg-white px-3.5 py-3 text-left transition-colors hover:bg-[rgba(12,26,51,0.02)] cursor-pointer"
                >
                  <span
                    className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-[var(--accent)]"
                    style={{
                      background: "color-mix(in srgb, var(--accent) 12%, #fff)",
                    }}
                  >
                    <Award size={17} aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-[var(--ink)]">
                      {title}
                    </span>
                    <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11.5px] text-[var(--muted)]">
                      {date && <span>{date}</span>}
                      {typeof row.total_score === "number" && (
                        <span>
                          · {t("dash.certs.score", { score: row.total_score })}
                        </span>
                      )}
                      <span className="ed-mono text-[10px] opacity-80">
                        · {row.certificate_number}
                      </span>
                    </span>
                  </span>
                  <ExternalLink
                    size={15}
                    className="shrink-0 text-[var(--muted)] transition-colors group-hover:text-[var(--ink)]"
                    aria-hidden="true"
                  />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {viewing && (
        <Certificate
          certificate={viewing}
          courseName={courseTitle(viewing, locale)}
          onClose={() => setViewing(null)}
        />
      )}
    </section>
  );
}

registerWidget({
  id: "generalCertificates",
  component: GeneralCertificatesWidgetImpl,
  supportedSlots: new Set(["mainColumn", "sideRail"]),
  displayName: "Certifications",
});

export const GeneralCertificatesWidget = GeneralCertificatesWidgetImpl;
